'use client';

import { Cancel, CheckCircle, Error } from '@mui/icons-material';
import { Alert, Box, Button, Grow, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSnackbarStore } from '../../store/snackbar';

const StyledAlert = styled(Alert)(({ theme, severity }) => {
	const baseStyles = {
		minHeight: '40px',
		display: 'flex',
		alignItems: 'center',
		fontSize: '14px',
		'& .MuiAlert-message': {
			padding: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			width: '100%'
		},
		'& .MuiAlert-action': {
			padding: 0,
			marginRight: 0
		}
	};

	// Custom info icon styles
	if (severity === 'info') {
		return {
			...baseStyles,
			backgroundColor: '#FFFFFF',
			color: '#1E2938',
			borderRadius: '12px',
			boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
			'& .MuiAlert-icon': {
				color: '#B85AFF',
				paddingTop: '3px'
			},
			'& .MuiAlert-action': {
				...baseStyles['& .MuiAlert-action'],
				color: '#5A6474'
			}
		};
	}

	// Same card layout as info; icon uses theme warning (MUI semantic)
	if (severity === 'warning') {
		return {
			...baseStyles,
			backgroundColor: '#FFFFFF',
			color: '#1E2938',
			borderRadius: '12px',
			boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
			'& .MuiAlert-icon': {
				color: theme.palette.warning.main,
				paddingTop: '3px'
			},
			'& .MuiAlert-action': {
				...baseStyles['& .MuiAlert-action'],
				color: '#5A6474'
			}
		};
	}

	// Success and error icon styles — on-brand palette (brand green / refined error red)
	return {
		...baseStyles,
		backgroundColor: severity === 'success' ? theme.palette.lumoraGreenPrimary.main : '#C62828',
		color: 'white',
		'& .MuiAlert-icon': {
			color: 'white'
		},
		'& .MuiAlert-action': {
			...baseStyles['& .MuiAlert-action'],
			color: 'white'
		}
	};
});

const ActionButton = styled(Button)(({ severity }) => ({
	color: severity === 'info' || severity === 'warning' ? '#1E2938' : 'white',
	fontSize: '12px',
	textTransform: 'uppercase',
	padding: '0 8px',
	minWidth: 'auto',
	'&:hover': {
		backgroundColor:
			severity === 'info' || severity === 'warning' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)'
	}
}));

// Created custom info icon to match Figma design
const InfoIcon = () => (
	<Box
		sx={{
			width: 22,
			height: 22,
			borderRadius: '50%',
			backgroundColor: '#B85AFF',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}>
		<Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>!</Typography>
	</Box>
);

const WarningIcon = () => (
	<Box
		sx={{
			width: 22,
			height: 22,
			borderRadius: '50%',
			bgcolor: 'warning.main',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}>
		<Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>!</Typography>
	</Box>
);

const getIcon = severity => {
	switch (severity) {
		case 'success':
			return <CheckCircle />;
		case 'info':
			return <InfoIcon />;
		case 'warning':
			return <WarningIcon />;
		case 'error':
			return <Error />;
		default:
			return <InfoIcon />; // Fallback info icon
	}
};

const snackShape = PropTypes.shape({
	id: PropTypes.number.isRequired,
	title: PropTypes.string,
	message: PropTypes.string,
	severity: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
	autoHideDuration: PropTypes.number,
	showUndo: PropTypes.bool,
	showView: PropTypes.bool,
	onUndo: PropTypes.func,
	onView: PropTypes.func,
	anchorOrigin: PropTypes.shape({
		vertical: PropTypes.oneOf(['top', 'bottom']),
		horizontal: PropTypes.oneOf(['left', 'center', 'right'])
	})
});

/** One toast in the stack: owns its auto-hide timer so each dismisses independently. */
const SnackbarItem = ({ snack, hideSnackbar }) => {
	const { id, severity = 'success', autoHideDuration } = snack;

	useEffect(() => {
		// 0/null means sticky — dismiss only via the close icon. Keyed on the toast id (zustand
		// actions are stable), so new toasts arriving can't reset an older toast's timer.
		if (!autoHideDuration) return undefined;
		const timer = setTimeout(() => hideSnackbar(id), autoHideDuration);
		return () => clearTimeout(timer);
	}, [id, autoHideDuration, hideSnackbar]);

	const onClose = () => hideSnackbar(id);

	return (
		<Grow in>
			<StyledAlert
				onClose={onClose}
				severity={severity}
				variant={severity === 'info' || severity === 'warning' ? 'standard' : 'filled'}
				icon={getIcon(severity)}
				action={
					<IconButton
						size="small"
						aria-label="Close notification"
						onClick={onClose}
						sx={{ color: 'inherit', p: 0 }}>
						<Cancel sx={{ fontSize: '20px' }} />
					</IconButton>
				}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, pr: 1 }}>
						{snack.title ? <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{snack.title}</Typography> : null}
						{snack.message ? <Typography sx={{ fontSize: '14px' }}>{snack.message}</Typography> : null}
					</Box>
					<Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
						{snack.showUndo && (
							<ActionButton severity={severity} onClick={snack.onUndo}>
								UNDO
							</ActionButton>
						)}
						{snack.showView && (
							<ActionButton severity={severity} onClick={snack.onView}>
								VIEW
							</ActionButton>
						)}
					</Box>
				</Box>
			</StyledAlert>
		</Grow>
	);
};

SnackbarItem.propTypes = {
	snack: snackShape.isRequired,
	hideSnackbar: PropTypes.func.isRequired
};

// Fixed container per anchor corner. Toasts render in arrival order: at a top anchor new ones
// append below (older toasts don't jump), at a bottom anchor new ones land nearest the edge.
const stackSx = ({ vertical = 'top', horizontal = 'center' }) => ({
	position: 'fixed',
	zIndex: 'snackbar',
	[vertical]: 24,
	display: 'flex',
	flexDirection: 'column',
	gap: 1,
	...(horizontal === 'center'
		? { left: '50%', transform: 'translateX(-50%)', alignItems: 'center' }
		: { [horizontal]: 24, alignItems: horizontal === 'right' ? 'flex-end' : 'flex-start' })
});

const GlobalSnackbar = () => {
	const { snackbars, hideSnackbar } = useSnackbarStore();

	if (snackbars.length === 0) return null;

	// One stack per anchor corner (in practice everything uses the default top-center).
	const groups = new Map();
	snackbars.forEach(snack => {
		const anchor = snack.anchorOrigin || { vertical: 'top', horizontal: 'center' };
		const key = `${anchor.vertical}-${anchor.horizontal}`;
		if (!groups.has(key)) groups.set(key, { anchor, items: [] });
		groups.get(key).items.push(snack);
	});

	return [...groups.entries()].map(([key, { anchor, items }]) => (
		<Box key={key} sx={stackSx(anchor)}>
			{items.map(snack => (
				<SnackbarItem key={snack.id} snack={snack} hideSnackbar={hideSnackbar} />
			))}
		</Box>
	));
};

export default GlobalSnackbar;

/**
 * SNACKBAR USAGE
 * ==============
 *
 * import { useSnackbarStore } from '../../store/snackbar';
 *
 * BASIC:
 * const { showSuccess, showError, showInfo } = useSnackbarStore();
 * showSuccess('Success message');
 * showError('Error message');
 * showInfo('Info message');
 *
 * Successive calls STACK (up to 4; the oldest is dropped beyond that) — each toast
 * auto-hides on its own timer.
 *
 * WITH ACTIONS:
 * showSuccess('Item deleted', {
 *   showUndo: true,
 *   onUndo: () => restoreItem()
 * });
 *
 * showError('Save failed', {
 *   showView: true,
 *   onView: () => showDetails()
 * });
 *
 * CUSTOM SETTINGS:
 * showSuccess('Custom message', {
 *   autoHideDuration: 3000, // 0/null = sticky until dismissed
 *   anchorOrigin: { vertical: 'top', horizontal: 'center' }
 * });
 *
 * MANUAL CONTROL:
 * const { hideSnackbar } = useSnackbarStore();
 * hideSnackbar();   // clears the whole stack
 * hideSnackbar(id); // dismisses one toast
 */
